// Workflow stage management utilities
import { WorkflowStage, WorkflowStageInfo, StageStatus } from '../types'

// Define the canonical order of workflow stages
export const WORKFLOW_STAGE_ORDER: WorkflowStage[] = [
  WorkflowStage.UPLOAD,
  WorkflowStage.MOBILE,
  WorkflowStage.STAMPZ,
  WorkflowStage.REGISTER,
  WorkflowStage.STICKERS,
  WorkflowStage.MAPPING,
  WorkflowStage.FIELDS,
  WorkflowStage.REVIEW,
  WorkflowStage.EXPORT,
]

// Initialize workflow stages array with all stages in pending state
export function initializeWorkflowStages(): WorkflowStageInfo[] {
  return WORKFLOW_STAGE_ORDER.map((stage) => ({
    stage,
    status: StageStatus.PENDING,
  }))
}

// Get stage info for a specific stage
export function getStageInfo(
  workflowStages: WorkflowStageInfo[],
  stage: WorkflowStage,
): WorkflowStageInfo | undefined {
  return workflowStages.find((s) => s.stage === stage)
}

// Update a stage's status
export function updateStageStatus(
  workflowStages: WorkflowStageInfo[],
  stage: WorkflowStage,
  status: StageStatus,
  notes?: string,
): WorkflowStageInfo[] {
  const now = new Date().toISOString()

  return workflowStages.map((s) => {
    if (s.stage !== stage) return s

    const updated: WorkflowStageInfo = { ...s, status }

    if (status === StageStatus.IN_PROGRESS && !s.startedAt) {
      updated.startedAt = now
    }

    if (status === StageStatus.COMPLETED) {
      updated.completedAt = now
    }

    if (status === StageStatus.SKIPPED) {
      updated.skippedAt = now
    }

    if (notes) {
      updated.notes = notes
    }

    return updated
  })
}

// Mark a stage as completed
export function completeStage(
  workflowStages: WorkflowStageInfo[],
  stage: WorkflowStage,
): WorkflowStageInfo[] {
  return updateStageStatus(workflowStages, stage, StageStatus.COMPLETED)
}

// Mark a stage as skipped
export function skipStage(
  workflowStages: WorkflowStageInfo[],
  stage: WorkflowStage,
  reason?: string,
): WorkflowStageInfo[] {
  return updateStageStatus(workflowStages, stage, StageStatus.SKIPPED, reason)
}

// Get the current stage (first non-completed, non-skipped stage)
export function getCurrentStage(workflowStages: WorkflowStageInfo[]): WorkflowStage {
  const currentStageInfo = workflowStages.find(
    (s) => s.status !== StageStatus.COMPLETED && s.status !== StageStatus.SKIPPED,
  )
  return currentStageInfo?.stage || WorkflowStage.EXPORT // Default to last stage if all completed
}

// Get the next stage after the given stage
export function getNextStage(currentStage: WorkflowStage): WorkflowStage | null {
  const currentIndex = WORKFLOW_STAGE_ORDER.indexOf(currentStage)
  if (currentIndex === -1 || currentIndex === WORKFLOW_STAGE_ORDER.length - 1) {
    return null
  }
  return WORKFLOW_STAGE_ORDER[currentIndex + 1]
}

// Calculate workflow progress (percentage)
export function getWorkflowProgress(workflowStages: WorkflowStageInfo[]): number {
  const completedCount = workflowStages.filter(
    (s) => s.status === StageStatus.COMPLETED || s.status === StageStatus.SKIPPED,
  ).length
  return Math.round((completedCount / workflowStages.length) * 100)
}

// Get completed stages array (for backward compatibility)
export function getCompletedStages(workflowStages: WorkflowStageInfo[]): WorkflowStage[] {
  return workflowStages.filter((s) => s.status === StageStatus.COMPLETED).map((s) => s.stage)
}

// Get skipped stages array (for backward compatibility)
export function getSkippedStages(workflowStages: WorkflowStageInfo[]): WorkflowStage[] {
  return workflowStages.filter((s) => s.status === StageStatus.SKIPPED).map((s) => s.stage)
}

// Check if a stage is completed
export function isStageCompleted(
  workflowStages: WorkflowStageInfo[],
  stage: WorkflowStage,
): boolean {
  const stageInfo = getStageInfo(workflowStages, stage)
  return stageInfo?.status === StageStatus.COMPLETED
}

// Check if a stage is skipped
export function isStageSkipped(workflowStages: WorkflowStageInfo[], stage: WorkflowStage): boolean {
  const stageInfo = getStageInfo(workflowStages, stage)
  return stageInfo?.status === StageStatus.SKIPPED
}

// Check if a stage is the current active stage
export function isCurrentStage(workflowStages: WorkflowStageInfo[], stage: WorkflowStage): boolean {
  return getCurrentStage(workflowStages) === stage
}

// Ensure loaded workflowStages includes all canonical stages in order.
// Stages missing from the saved data (e.g. mobile added after template was created)
// are inserted as pending at the correct position.
export function normalizeWorkflowStages(saved: WorkflowStageInfo[]): WorkflowStageInfo[] {
  const byStage = new Map(saved.map((s) => [s.stage, s]))
  return WORKFLOW_STAGE_ORDER.map(
    (stage) => byStage.get(stage) || { stage, status: StageStatus.PENDING },
  )
}

// Migrate from old completedStages/skippedStages arrays to workflowStages
export function migrateToWorkflowStages(
  completedStages: WorkflowStage[] = [],
  skippedStages: WorkflowStage[] = [],
): WorkflowStageInfo[] {
  return WORKFLOW_STAGE_ORDER.map((stage) => {
    if (completedStages.includes(stage)) {
      return {
        stage,
        status: StageStatus.COMPLETED,
        completedAt: new Date().toISOString(),
      }
    }

    if (skippedStages.includes(stage)) {
      return {
        stage,
        status: StageStatus.SKIPPED,
        skippedAt: new Date().toISOString(),
      }
    }

    return {
      stage,
      status: StageStatus.PENDING,
    }
  })
}

// Get summary of workflow stages
export function getWorkflowSummary(workflowStages: WorkflowStageInfo[]): {
  total: number
  completed: number
  skipped: number
  pending: number
  inProgress: number
  progress: number
  stagesRemaining: number
} {
  const completed = workflowStages.filter((s) => s.status === StageStatus.COMPLETED).length
  const skipped = workflowStages.filter((s) => s.status === StageStatus.SKIPPED).length
  const pending = workflowStages.filter((s) => s.status === StageStatus.PENDING).length
  const inProgress = workflowStages.filter((s) => s.status === StageStatus.IN_PROGRESS).length
  const total = workflowStages.length
  const progress = getWorkflowProgress(workflowStages)
  const stagesRemaining = pending + inProgress

  return {
    total,
    completed,
    skipped,
    pending,
    inProgress,
    progress,
    stagesRemaining,
  }
}
